import React, { useState, FC } from 'react';

import { AxiosInstance } from 'axios';
import { noop } from 'lodash';
import { createBridgeComponent } from 'mfe-react-bridge/react17';
import { Button } from 'mintel-react-ui';

import { cwaUrl } from '/configuration';
import { CustomPanelType, PublishStatus } from '/types';
import { buildArticleApiRelativeUrl, useCwaAxios } from '/utils/cwaAxios';

import useTranslate from '../../../utils/translations';
import useTracking from '../../../utils/useTracking';
import styles from './LongTermPreviewPanel.scss';

type LongTermPreviewPanelProps = {
  cwaAxios: AxiosInstance;
  isLongTermPreview: boolean;
  articleId: string;
  trackLongTermPreview: (action: string) => void;
  t: CallableFunction;
};

const LongTermPreviewPanelComponent: FC<LongTermPreviewPanelProps> = ({
  cwaAxios,
  isLongTermPreview,
  articleId,
  trackLongTermPreview,
  t,
}) => {
  const [active, setActive] = useState(true);

  const contentLocation = {
    publishStatus: 'long_term_preview' as PublishStatus,
    contentType: 'article',
    slugOrId: articleId,
  };

  const handleClick = async (): Promise<void> => {
    setActive(false);
    const requestMethod = isLongTermPreview ? 'delete' : 'post';
    trackLongTermPreview(requestMethod);
    await cwaAxios[requestMethod](buildArticleApiRelativeUrl(contentLocation));
    if (isLongTermPreview) {
      window.location.href = cwaUrl; // Redirect after successful deletion
    }
  };

  const Btn = isLongTermPreview ? Button.DangerButton : Button.ActionButton;
  return (
    <div className={styles.container}>
      <Btn handleClick={handleClick} disabled={!active}>
        {isLongTermPreview ? t('Delete') : t('Generate')}
      </Btn>
    </div>
  );
};

const useLongTermPreviewPanel = (
  isLongTermPreview: boolean,
  articleId: string,
): CustomPanelType => {
  const cwaAxios = useCwaAxios();
  const t = useTranslate();
  const { trackLongTermPreview } = useTracking();

  return {
    bridge: {
      default: createBridgeComponent({
        rootComponent: (): JSX.Element => (
          <LongTermPreviewPanelComponent
            cwaAxios={cwaAxios}
            isLongTermPreview={isLongTermPreview}
            articleId={articleId}
            trackLongTermPreview={trackLongTermPreview}
            t={t}
          />
        ),
      }),
    },
    closeSidebar: noop,
    iconName: 'eye',
    color: 'quaternary',
    title: t('Long Term Preview'),
    subtitle: isLongTermPreview
      ? t('Delete this Long Term Preview.')
      : t(
          'Generate a Long Term Preview for this piece. Long term previews expire after a month and are removed once the piece is published.',
        ),
    width: 400,
    id: 'long-term-preview-panel',
  };
};

export default useLongTermPreviewPanel;
